/**
 * Wire protocol of GET /ws, mirrored from control/internal/httpapi/ws.go + hub.go
 * (docs/contract.md). Every server-originated frame carries a per-connection delivery `seq`
 * that starts at 1 and advances by exactly one per delivered frame; the client detects gaps and
 * resynchronises through a reconnect. Parsing is strict on types and lenient on unknown extra
 * fields: a frame that violates its shape is dropped (null), never misinterpreted.
 */

/** Participant role vocabulary (db 0004 migration, carried verbatim on the wire). */
export type ParticipantRole = 'admin' | 'moderator' | 'participant';

/**
 * Room kind vocabulary (ADR 0010, CR-12, as revised by the workshop merge): the join response
 * and the WS state frame carry the authoritative kind; a missing or unrecognized value reads
 * as meeting, mirroring the server's own normalizeRoomKind. The retired 'workshop' value
 * (migration 0015 converted the rows) clamps to meeting like any unknown value.
 */
export const ROOM_KINDS = ['meeting', 'webinar'] as const;

export type RoomKind = (typeof ROOM_KINDS)[number];

/** Narrows an unknown wire value to the room-kind vocabulary; anything else clamps to meeting. */
export function parseRoomKind(value: unknown): RoomKind {
  return value === 'meeting' || value === 'webinar' ? value : 'meeting';
}

/**
 * Roles a client may assign through a role frame (moderation.go assignableRole): admin is not
 * assignable - admins are the room owners only, and a role frame asking for admin is answered
 * with invalid_request.
 */
export type AssignableRole = 'moderator' | 'participant';

/** Actions of the moderate client frame (moderation.go wire vocabulary). `end` is the
 * staff-only end-for-everyone decision (the unified room bars): target-less, room-wide. */
export type ModerationAction = 'mute' | 'mute_all' | 'kick' | 'stop_share' | 'end';

/**
 * Actions of the participants-panel menu: moderate actions plus the role assignments. The
 * room-wide `end` decision never appears in a per-participant menu, so it stays out of this
 * union even though it shares the moderate frame.
 */
export type PanelModerationAction =
  'mute' | 'mute_all' | 'kick' | 'stop_share' | 'promote' | 'demote';

/** Roles that get a visible badge; plain participants render without one. */
export function isBadgedRole(role: ParticipantRole): boolean {
  return role === 'admin' || role === 'moderator';
}

/**
 * The fixed reaction allowlist, exactly the eight codepoints the server accepts
 * (ws.go reactionEmojis): thumbs-up, clap, heart (U+2764, no variation selector),
 * laugh, wow, sad, wave, party.
 */
export const REACTION_EMOJIS: readonly string[] = [
  '\u{1F44D}',
  '\u{1F44F}',
  '\u2764',
  '\u{1F602}',
  '\u{1F62E}',
  '\u{1F622}',
  '\u{1F44B}',
  '\u{1F389}',
];

const REACTION_SET: ReadonlySet<string> = new Set(REACTION_EMOJIS);

export function isAllowedReaction(emoji: string): boolean {
  return REACTION_SET.has(emoji);
}

/**
 * The closed vocabulary of structured system chat rows (db 0007, control ws.go
 * systemChatKind): every server-created system row carries its kind and display-name params
 * alongside the canonical English body, so the client renders the row in the UI language.
 */
export const SYSTEM_CHAT_KINDS = [
  'joined',
  'left',
  'muted',
  'mute_all',
  'kicked',
  'asked_stop_share',
  'promoted',
  'demoted',
  'recording_started',
  'recording_stopped',
] as const;

export type SystemChatKind = (typeof SYSTEM_CHAT_KINDS)[number];

const SYSTEM_CHAT_KIND_SET: ReadonlySet<string> = new Set(SYSTEM_CHAT_KINDS);

export function isSystemChatKind(value: unknown): value is SystemChatKind {
  return typeof value === 'string' && SYSTEM_CHAT_KIND_SET.has(value);
}

/** The params each system kind interpolates; a row missing any of them degrades to its body. */
const SYSTEM_CHAT_REQUIRED_PARAMS: Readonly<Record<SystemChatKind, readonly string[]>> = {
  joined: ['name'],
  left: ['name'],
  muted: ['actor', 'target'],
  mute_all: ['actor'],
  kicked: ['actor', 'target'],
  asked_stop_share: ['actor', 'target'],
  promoted: ['target'],
  demoted: ['target'],
  recording_started: ['actor'],
  recording_stopped: ['actor'],
};

/**
 * Parses the optional structured system-row metadata carried by every chat row shape (WS
 * frames and REST history rows). Metadata that violates the closed contract - an unknown kind
 * (a newer server talking to an older client), non-string params, or params missing a key the
 * kind's template interpolates - degrades to no metadata at all instead of dropping the row:
 * the canonical English body always renders.
 */
export function parseSystemChatMeta(value: Record<string, unknown>): {
  kind?: SystemChatKind;
  params?: Record<string, string>;
} {
  if (!isSystemChatKind(value.kind)) {
    return {};
  }
  const raw = value.params;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return {};
  }
  const params: Record<string, string> = {};
  for (const [key, entry] of Object.entries(raw)) {
    if (typeof entry !== 'string') {
      // One non-string value degrades the whole map: partial interpolation would be worse.
      return {};
    }
    params[key] = entry;
  }
  for (const key of SYSTEM_CHAT_REQUIRED_PARAMS[value.kind]) {
    if (!(key in params)) {
      return {};
    }
  }
  return { kind: value.kind, params };
}

/** One persisted chat row: the wire shape of chat rows everywhere (WS frames and REST history). */
export interface ChatMessage {
  readonly id: number;
  /** Empty for system rows (join/leave and moderation notices) - nobody authored them. */
  readonly identity: string;
  readonly displayName: string;
  readonly body: string;
  /** RFC 3339 timestamp as persisted; formatted HH:MM in the UI. */
  readonly ts: string;
  readonly system: boolean;
  /** Closed system-row kind (0007); absent for ordinary rows and rows persisted before it. */
  readonly kind?: SystemChatKind;
  /** Display-name params of a structured system row; present only alongside a kind. */
  readonly params?: Readonly<Record<string, string>>;
}

/** One member of the room presence snapshot. */
export interface ParticipantState {
  readonly identity: string;
  readonly displayName: string;
  readonly role: ParticipantRole;
  readonly handRaised: boolean;
}

/**
 * Control-minted TURN entry (control rooms.go turnSection), carried by the join response and by
 * the lobby_admitted frame (CR-15) when the control service runs with CONTROL_TURN_URL
 * configured. LiveKit's own injected ICE servers announce an unusable `turns:<domain>:443`
 * behind the stand's reverse proxy, so the web app feeds this entry to the SDK as RTCIceServer
 * config instead (the TURN server doubles as STUN).
 */
export interface TurnCredentials {
  /** TURN/STUN URLs; every entry starts with `turns:` or `turn:`. */
  readonly urls: readonly string[];
  readonly username: string;
  readonly credential: string;
  /** Same format as the join response's expiresAt; optional on the wire. */
  readonly expiresAt?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Narrows an unknown `turn` section to {@link TurnCredentials}. Returns undefined for an absent
 * section and for every malformed variant: the app must work identically on stands without TURN,
 * so a bad section degrades to "no TURN" instead of failing the whole join.
 */
export function parseTurnCredentials(value: unknown): TurnCredentials | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  // Array.isArray narrows unknown to any[]; retype to unknown[] immediately so every entry is
  // narrowed explicitly below (the project forbids implicit any).
  if (!Array.isArray(value.urls) || value.urls.length === 0) {
    return undefined;
  }
  const rawUrls: unknown[] = value.urls;
  const urls: string[] = [];
  for (const url of rawUrls) {
    if (typeof url !== 'string' || url.length === 0) {
      return undefined;
    }
    if (!url.startsWith('turns:') && !url.startsWith('turn:')) {
      return undefined;
    }
    urls.push(url);
  }
  if (typeof value.username !== 'string' || value.username.length === 0) {
    return undefined;
  }
  if (typeof value.credential !== 'string' || value.credential.length === 0) {
    return undefined;
  }
  const turn: {
    urls: string[];
    username: string;
    credential: string;
    expiresAt?: string;
  } = { urls, username: value.username, credential: value.credential };
  if (value.expiresAt !== undefined) {
    if (typeof value.expiresAt !== 'string' || value.expiresAt.length === 0) {
      return undefined;
    }
    turn.expiresAt = value.expiresAt;
  }
  return turn;
}

/** The `you` section of a state frame: the authenticated identity and its role. */
export interface YouState {
  readonly identity: string;
  readonly role: ParticipantRole;
}

export interface StateFrame {
  readonly type: 'state';
  readonly seq: number;
  readonly participants: readonly ParticipantState[];
  readonly you: YouState;
  /** The room's kind (CR-12); absent on pre-CR-12 servers and clamps to meeting. */
  readonly kind: RoomKind;
}

export interface ChatHistoryFrame {
  readonly type: 'chat_history';
  readonly seq: number;
  readonly messages: readonly ChatMessage[];
  readonly hasMore: boolean;
}

export interface ParticipantJoinedFrame {
  readonly type: 'participant_joined';
  readonly seq: number;
  readonly identity: string;
  readonly displayName: string;
  readonly role: ParticipantRole;
}

export interface ParticipantLeftFrame {
  readonly type: 'participant_left';
  readonly seq: number;
  readonly identity: string;
}

export interface HandFrame {
  readonly type: 'hand';
  readonly seq: number;
  readonly identity: string;
  readonly raised: boolean;
}

export interface ReactionFrame {
  readonly type: 'reaction';
  readonly seq: number;
  readonly identity: string;
  readonly emoji: string;
}

export interface ChatFrame {
  readonly type: 'chat';
  readonly seq: number;
  readonly message: ChatMessage;
}

export interface RoleFrame {
  readonly type: 'role';
  readonly seq: number;
  readonly identity: string;
  readonly role: ParticipantRole;
}

/** Server-to-target control frame of the cooperative moderation actions ("stop_share" today). */
export interface ModerationFrame {
  readonly type: 'moderation';
  readonly seq: number;
  readonly action: string;
}

/**
 * The closed recording-state vocabulary of the `recording` frame (moderation.go recordingFrame,
 * db 0008 statuses): starting -> recording -> stopping -> completed|failed.
 */
export const RECORDING_STATES = [
  'starting',
  'recording',
  'stopping',
  'completed',
  'failed',
] as const;

export type RecordingState = (typeof RECORDING_STATES)[number];

/** Narrows an unknown wire value to the recording-state vocabulary; anything else is dropped. */
export function parseRecordingState(value: unknown): RecordingState | null {
  return value === 'starting' ||
    value === 'recording' ||
    value === 'stopping' ||
    value === 'completed' ||
    value === 'failed'
    ? value
    : null;
}

/** True while a recording is still in flight (the indicator shows; the room is being recorded). */
export function isRecordingActive(state: RecordingState): boolean {
  return state === 'starting' || state === 'recording' || state === 'stopping';
}

/** Room-wide recording announcement (moderation.go recordingFrame), broadcast on every transition. */
export interface RecordingFrame {
  readonly type: 'recording';
  readonly seq: number;
  readonly state: RecordingState;
  readonly recordingId: number;
}

/**
 * The staff end-for-everyone decision (moderation.go meetingEndedFrame, the unified room
 * bars): broadcast to every member right before the hub closes each connection with 4406 and
 * deletes the SFU room. `by`/`byName` identify the staff member who ended the meeting, so a
 * client can word the actor's own screen differently from everyone else's.
 */
export interface MeetingEndedFrame {
  readonly type: 'meeting_ended';
  readonly seq: number;
  readonly by: string;
  readonly byName: string;
}

export interface ErrorFrame {
  readonly type: 'error';
  readonly seq: number;
  readonly code: string;
}

/**
 * One Q&A question as the wire carries it (control qa.go qaQuestionState). The broadcast of
 * each action embeds the question's full state columns: ask/answer replay every field, an
 * upvote only refreshes the count and a dismiss only flags - the untouched columns ride as
 * zero values (Go serializes them without omitempty), so only `id` and the flags/count are
 * strictly required on the wire and the text fields clamp to '' when absent.
 */
export interface QAQuestion {
  readonly id: number;
  readonly identity: string;
  readonly displayName: string;
  readonly text: string;
  readonly upvotes: number;
  readonly answered: boolean;
  /** Identity of the answering moderator; absent (omitted) while unanswered. */
  readonly answeredBy?: string | undefined;
  /** Optional answer text a moderator attaches when marking the question answered. */
  readonly answerText?: string | undefined;
  readonly dismissed: boolean;
  /** RFC 3339 creation timestamp; '' on the partial upvote/dismiss broadcasts. */
  readonly ts: string;
}

/** Actions of the bidirectional qa frame (control ws.go qaAction*). */
export type QAAction = 'ask' | 'upvote' | 'answer' | 'dismiss';

/** One Q&A change (control qa.go qaFrame): ask/upvote/answer/dismiss, sender included. */
export interface QAFrame {
  readonly type: 'qa';
  readonly seq: number;
  readonly action: QAAction;
  readonly question: QAQuestion;
}

/** The handshake replay of the question board (webinar rooms only, ascending by id). */
export interface QAHistoryFrame {
  readonly type: 'qa_history';
  readonly seq: number;
  readonly questions: readonly QAQuestion[];
}

/** A speak-grant change (control speak.go speakFrame), broadcast to every member. */
export interface SpeakGrantFrame {
  readonly type: 'speak_grant';
  readonly seq: number;
  readonly identity: string;
  readonly granted: boolean;
}

/**
 * One editor's incremental scene change, fanned out to every OTHER member (control board.go
 * boardUpdateFrame, ADR 0013): `identity` names the editor, `elements` are the raw Excalidraw
 * element snapshots exactly as the sender sent them (the hub merges by element id and never
 * interprets them). There is no echo: the sender applied the change locally already.
 */
export interface BoardUpdateFrame {
  readonly type: 'board_update';
  readonly seq: number;
  readonly identity: string;
  readonly elements: readonly unknown[];
}

/**
 * The room's whole merged scene, delivered once per connection right after the handshake
 * snapshot when the room's board is non-empty (control board.go boardStateFrame, ADR 0013);
 * `updatedBy` names the last editor the hub saw and is omitted for a scene reloaded from the
 * database.
 */
export interface BoardStateFrame {
  readonly type: 'board_state';
  readonly seq: number;
  readonly elements: readonly unknown[];
  readonly updatedBy?: string | undefined;
}

/**
 * A board-editor grant change, broadcast to every member (the speak_grant shape): the target
 * client derives from it whether its board renders read-only.
 */
export interface BoardGrantFrame {
  readonly type: 'board_grant';
  readonly seq: number;
  readonly identity: string;
  readonly granted: boolean;
}

/**
 * The room's board-broadcast switch (the board surface amendment, control board.go
 * boardSurfaceFrame): fanned out to every member when a staff member toggles the board as the
 * room's shared stage, and replayed to a late joiner right after the handshake snapshot while
 * the surface is open. The board itself stays gated by ADR 0013 - the frame only decides
 * whether the board is the room's shared stage.
 */
export interface BoardSurfaceFrame {
  readonly type: 'board_surface';
  readonly seq: number;
  readonly open: boolean;
}

/** One flushed cursor of the board_pointer batch (CR-18, control board_pointer.go). */
export interface BoardPointerEntry {
  readonly identity: string;
  /** Cursor position in Excalidraw scene coordinates (server-clamped to the board bounds). */
  readonly x: number;
  readonly y: number;
}

/**
 * The room's coalesced live-cursor batch (CR-18): flushed at most once per ~100 ms per room,
 * each member receiving every OTHER editor's latest position. Purely ephemeral.
 */
export interface BoardPointerFrame {
  readonly type: 'board_pointer';
  readonly seq: number;
  readonly pointers: readonly BoardPointerEntry[];
}

/**
 * One identity waiting for a staff lobby decision (CR-14, control lobby.go lobbyItem). The
 * requestedAt stamp is the hub's RFC 3339 join-attempt time; the panel shows it as a waiting
 * duration.
 */
export interface LobbyPendingItem {
  readonly identity: string;
  readonly displayName: string;
  readonly requestedAt: string;
}

/** Server-to-a-held-connection frame (CR-14): you are in the lobby; wait for a staff decision. */
export interface LobbyWaitFrame {
  readonly type: 'lobby_wait';
  readonly seq: number;
  readonly room: string;
}

/** Server-to-staff frame (CR-14): one more identity joined the waiting list. */
export interface LobbyWaitingFrame {
  readonly type: 'lobby_waiting';
  readonly seq: number;
  readonly item: LobbyPendingItem;
}

/** Server-to-(re)connecting-staff frame (CR-14): the whole waiting list, in hold order. */
export interface LobbyPendingFrame {
  readonly type: 'lobby_pending';
  readonly seq: number;
  readonly items: readonly LobbyPendingItem[];
}

/**
 * The last frame a denied connection receives before the server closes it with 4407 (CR-14);
 * the client also treats the close itself as the denial signal, so a close that races the
 * frame still lands.
 */
export interface LobbyDeniedFrame {
  readonly type: 'lobby_denied';
  readonly seq: number;
}

/**
 * The media credential set an admitted held join receives (CR-15, control lobby.go
 * lobbyAdmittedFrame): the same shape a direct join response carries - token, SFU url, expiry
 * and, when the stand mints one, the TURN section. The held join's REST response answered none
 * of it; this frame is where the media credentials come from.
 */
export interface LobbyAdmittedMedia {
  readonly token: string;
  readonly url: string;
  /** RFC 3339 expiry of the minted token (matches the token exp claim). */
  readonly expiresAt: string;
  /** Present only when the control service mints TURN credentials. */
  readonly turn?: TurnCredentials | undefined;
}

/**
 * Server-to-the-admitted-connection frame (CR-15): the deferred media credentials, delivered as
 * the FIRST frame of the admit - before the post-auth join sequence - so the client starts its
 * media connect while the handshake frames flow.
 */
export interface LobbyAdmittedFrame {
  readonly type: 'lobby_admitted';
  readonly seq: number;
  readonly credentials: LobbyAdmittedMedia;
}

/**
 * Server-to-staff frame (CR-16): a waiting identity was decided (by any staff member, admit or
 * deny). Every staff panel drops the decided row on it; for the deciding member the frame is a
 * no-op after its own optimistic drop.
 */
export interface LobbyResolvedFrame {
  readonly type: 'lobby_resolved';
  readonly seq: number;
  readonly identity: string;
  readonly admitted: boolean;
}

/** Error codes carried in error frames (ws.go). */
export type ErrorCode = 'rate_limited' | 'storage_error' | 'invalid_request' | string;

export type ServerFrame =
  | StateFrame
  | ChatHistoryFrame
  | ParticipantJoinedFrame
  | ParticipantLeftFrame
  | HandFrame
  | ReactionFrame
  | ChatFrame
  | RoleFrame
  | ModerationFrame
  | RecordingFrame
  | MeetingEndedFrame
  | QAFrame
  | QAHistoryFrame
  | SpeakGrantFrame
  | BoardUpdateFrame
  | BoardStateFrame
  | BoardGrantFrame
  | BoardSurfaceFrame
  | BoardPointerFrame
  | LobbyWaitFrame
  | LobbyWaitingFrame
  | LobbyPendingFrame
  | LobbyAdmittedFrame
  | LobbyDeniedFrame
  | LobbyResolvedFrame
  | ErrorFrame;

/** Actions of the client qa frame; ask/upvote carry text/questionId respectively. */
export type ClientQAAction = QAAction;

/** Client-to-server frames. auth must be the first frame on every connection. */
export type ClientFrame =
  | { readonly type: 'auth'; readonly token: string }
  | { readonly type: 'hand'; readonly raised: boolean }
  | { readonly type: 'react'; readonly emoji: string }
  | { readonly type: 'chat'; readonly body: string }
  | { readonly type: 'moderate'; readonly action: ModerationAction; readonly target: string }
  | { readonly type: 'role'; readonly target: string; readonly role: AssignableRole }
  | {
      readonly type: 'qa';
      readonly action: ClientQAAction;
      /** Question/answer text of the ask and answer actions (1..500 runes after trimming). */
      readonly text?: string | undefined;
      /** Target question of the upvote/answer/dismiss actions. */
      readonly questionId?: number | undefined;
    }
  | {
      /**
       * One board scene chunk (control board.go wsHandleBoardUpdate, ADR 0013): the raw
       * Excalidraw element snapshots whose version grew since the last broadcast. Editors
       * only - the server answers everyone else with error{forbidden} and keeps the
       * connection. Element body validation is server-side; the client sends what the
       * reconciler produced.
       */
      readonly type: 'board_update';
      readonly elements: readonly unknown[];
    }
  | {
      /**
       * The sender's live cursor position (CR-18, control board_pointer.go): Excalidraw scene
       * coordinates, throttled client-side to ~30 Hz. Editors only - the server answers
       * everyone else with error{forbidden} and keeps the connection; out-of-range values
       * clamp server-side into the board bounds.
       */
      readonly type: 'board_pointer';
      readonly x: number;
      readonly y: number;
    }
  | {
      /**
       * Staff-only board-broadcast switch (the board surface amendment): open or close the
       * board as the room's shared stage. A non-staff sender is answered error{forbidden}; the
       * hub stores the switch and fans the new state out to every member, sender included.
       */
      readonly type: 'board_surface';
      readonly open: boolean;
    }
  | {
      /**
       * Staff-only lobby decision (CR-14): let this waiting identity into the room. The
       * identity is matched against the room's waiting list server-side; unknown or
       * already-resolved identities answer error{invalid_request}. Participants are answered
       * error{forbidden}.
       */
      readonly type: 'lobby_admit';
      readonly identity: string;
    }
  | {
      /** Staff-only lobby decision (CR-14): refuse this waiting identity (close 4407 follows). */
      readonly type: 'lobby_deny';
      readonly identity: string;
    };

/** Error codes a rejected moderation or role request produces (moderation.go). */
export const MODERATION_ERROR_CODES: readonly string[] = [
  'forbidden',
  'invalid_request',
  'moderation_failed',
];

/** Chat send contract (server: trim, 1..2000 runes, 5 per 10 s). */
export const CHAT_MAX_RUNES = 2000;

/** Returns the trimmed body when it fits the contract, null otherwise. */
export function sanitizeChatBody(input: string): string | null {
  const body = input.trim();
  if (body.length === 0 || body.length > CHAT_MAX_RUNES) {
    return null;
  }
  return body;
}

/** Q&A text contract (control ws.go maxQABodyRunes): trim, 1..500 runes. */
export const QA_MAX_RUNES = 500;

/** Returns the trimmed text when it fits the Q&A contract, null otherwise. */
export function sanitizeQAText(input: string): string | null {
  const text = input.trim();
  if (text.length === 0 || text.length > QA_MAX_RUNES) {
    return null;
  }
  return text;
}

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function bool(value: unknown): boolean {
  return value === true;
}

function role(value: unknown): ParticipantRole | null {
  return value === 'admin' || value === 'moderator' || value === 'participant' ? value : null;
}

function required(value: unknown): string | null {
  const s = str(value);
  return s !== null && s.length > 0 ? s : null;
}

function parseSeq(record: Record<string, unknown>): number | null {
  const seq = num(record.seq);
  if (seq === null || !Number.isInteger(seq) || seq < 0) {
    return null;
  }
  return seq;
}

const QA_ACTIONS: ReadonlySet<string> = new Set(['ask', 'upvote', 'answer', 'dismiss']);

/** Type guard for the closed qa action vocabulary. */
function isQAAction(value: string): value is QAAction {
  return QA_ACTIONS.has(value);
}

/**
 * Parses one wire question (qa.go qaQuestionState): id, upvotes, answered and dismissed are the
 * strictly-required columns (Go serializes them without omitempty); the text columns clamp to ''
 * and the answeredBy/answerText pair stays optional. A non-positive id is a shape violation.
 */
function parseQAQuestion(value: unknown): QAQuestion | null {
  if (!isRecord(value)) {
    return null;
  }
  const id = num(value.id);
  const upvotes = num(value.upvotes);
  if (
    id === null ||
    !Number.isInteger(id) ||
    id <= 0 ||
    upvotes === null ||
    !Number.isInteger(upvotes) ||
    upvotes < 0 ||
    typeof value.answered !== 'boolean' ||
    typeof value.dismissed !== 'boolean'
  ) {
    return null;
  }
  const answeredBy = typeof value.answeredBy === 'string' ? value.answeredBy : undefined;
  const answerText = typeof value.answerText === 'string' ? value.answerText : undefined;
  return {
    id,
    identity: str(value.identity) ?? '',
    displayName: str(value.displayName) ?? '',
    text: str(value.text) ?? '',
    upvotes,
    answered: value.answered,
    answeredBy,
    answerText,
    dismissed: value.dismissed,
    ts: str(value.ts) ?? '',
  };
}

function parseChatMessage(value: unknown): ChatMessage | null {
  if (!isRecord(value)) {
    return null;
  }
  const id = num(value.id);
  const body = str(value.body);
  const ts = str(value.ts);
  if (id === null || !Number.isInteger(id) || body === null || ts === null) {
    return null;
  }
  return {
    id,
    identity: str(value.identity) ?? '',
    displayName: str(value.displayName) ?? '',
    body,
    ts,
    system: bool(value.system),
    ...parseSystemChatMeta(value),
  };
}

function parseParticipant(value: unknown): ParticipantState | null {
  if (!isRecord(value)) {
    return null;
  }
  const identity = required(value.identity);
  const participantRole = role(value.role);
  if (identity === null || participantRole === null) {
    return null;
  }
  return {
    identity,
    displayName: str(value.displayName) ?? '',
    role: participantRole,
    handRaised: bool(value.handRaised),
  };
}

/**
 * Parses one entry of the lobby waiting list (control lobby.go lobbyItem): identity and the
 * RFC 3339 requestedAt stamp are required; the display name clamps to '' like everywhere else.
 */
export function parseLobbyItem(value: unknown): LobbyPendingItem | null {
  if (!isRecord(value)) {
    return null;
  }
  const identity = required(value.identity);
  const requestedAt = required(value.requestedAt);
  if (identity === null || requestedAt === null) {
    return null;
  }
  return {
    identity,
    displayName: str(value.displayName) ?? '',
    requestedAt,
  };
}

/**
 * Parses one raw WebSocket text payload into a typed server frame. Returns null for anything
 * that is not a frame of this protocol (unknown types, malformed JSON, shape violations) - the
 * connection survives, the frame is dropped.
 */
export function parseServerFrame(raw: unknown): ServerFrame | null {
  if (!isRecord(raw)) {
    return null;
  }
  const seq = parseSeq(raw);
  const type = str(raw.type);
  if (seq === null || type === null) {
    return null;
  }
  switch (type) {
    case 'state': {
      if (!Array.isArray(raw.participants) || !isRecord(raw.you)) {
        return null;
      }
      const identity = required(raw.you.identity);
      const youRole = role(raw.you.role);
      if (identity === null || youRole === null) {
        return null;
      }
      const participants: ParticipantState[] = [];
      for (const entry of raw.participants) {
        const participant = parseParticipant(entry);
        if (participant === null) {
          return null;
        }
        participants.push(participant);
      }
      return {
        type: 'state',
        seq,
        participants,
        you: { identity, role: youRole },
        kind: parseRoomKind(raw.kind),
      };
    }
    case 'chat_history': {
      if (!Array.isArray(raw.messages) || typeof raw.hasMore !== 'boolean') {
        return null;
      }
      const messages: ChatMessage[] = [];
      for (const entry of raw.messages) {
        const message = parseChatMessage(entry);
        if (message === null) {
          return null;
        }
        messages.push(message);
      }
      return { type: 'chat_history', seq, messages, hasMore: raw.hasMore };
    }
    case 'participant_joined': {
      const identity = required(raw.identity);
      const joinedRole = role(raw.role);
      if (identity === null || joinedRole === null) {
        return null;
      }
      return {
        type: 'participant_joined',
        seq,
        identity,
        displayName: str(raw.displayName) ?? '',
        role: joinedRole,
      };
    }
    case 'participant_left': {
      const identity = required(raw.identity);
      return identity === null ? null : { type: 'participant_left', seq, identity };
    }
    case 'hand': {
      const identity = required(raw.identity);
      if (identity === null || typeof raw.raised !== 'boolean') {
        return null;
      }
      return { type: 'hand', seq, identity, raised: raw.raised };
    }
    case 'reaction': {
      const identity = required(raw.identity);
      const emoji = str(raw.emoji);
      // Off-allowlist emojis never come from this server; drop them like any malformed frame.
      if (identity === null || emoji === null || !isAllowedReaction(emoji)) {
        return null;
      }
      return { type: 'reaction', seq, identity, emoji };
    }
    case 'chat': {
      const message = parseChatMessage(raw.message ?? raw);
      // The wire embeds the chat row inline (chatFrame embeds chatMessage); a nested
      // `message` object is accepted too for forward compatibility.
      return message === null ? null : { type: 'chat', seq, message };
    }
    case 'role': {
      const identity = required(raw.identity);
      const newRole = role(raw.role);
      return identity === null || newRole === null
        ? null
        : { type: 'role', seq, identity, role: newRole };
    }
    case 'moderation': {
      const action = str(raw.action);
      return action === null ? null : { type: 'moderation', seq, action };
    }
    case 'recording': {
      const recordingState = parseRecordingState(raw.state);
      const recordingId = num(raw.recordingId);
      if (
        recordingState === null ||
        recordingId === null ||
        !Number.isInteger(recordingId) ||
        recordingId < 0
      ) {
        return null;
      }
      return { type: 'recording', seq, state: recordingState, recordingId };
    }
    case 'meeting_ended': {
      const by = required(raw.by);
      const byName = str(raw.byName);
      if (by === null || byName === null) {
        return null;
      }
      return { type: 'meeting_ended', seq, by, byName };
    }
    case 'error': {
      const code = str(raw.code);
      return code === null ? null : { type: 'error', seq, code };
    }
    case 'qa': {
      const action = str(raw.action);
      const question = parseQAQuestion(raw.question);
      if (action === null || !isQAAction(action) || question === null) {
        return null;
      }
      return { type: 'qa', seq, action, question };
    }
    case 'qa_history': {
      if (!Array.isArray(raw.questions)) {
        return null;
      }
      const questions: QAQuestion[] = [];
      for (const entry of raw.questions) {
        const question = parseQAQuestion(entry);
        if (question === null) {
          return null;
        }
        questions.push(question);
      }
      return { type: 'qa_history', seq, questions };
    }
    case 'speak_grant': {
      const identity = required(raw.identity);
      if (identity === null || typeof raw.granted !== 'boolean') {
        return null;
      }
      return { type: 'speak_grant', seq, identity, granted: raw.granted };
    }
    case 'board_update': {
      const identity = required(raw.identity);
      if (identity === null || !Array.isArray(raw.elements)) {
        return null;
      }
      return { type: 'board_update', seq, identity, elements: [...raw.elements] };
    }
    case 'board_state': {
      if (!Array.isArray(raw.elements)) {
        return null;
      }
      return {
        type: 'board_state',
        seq,
        elements: [...raw.elements],
        ...(typeof raw.updatedBy === 'string' ? { updatedBy: raw.updatedBy } : {}),
      };
    }
    case 'board_grant': {
      const identity = required(raw.identity);
      if (identity === null || typeof raw.granted !== 'boolean') {
        return null;
      }
      return { type: 'board_grant', seq, identity, granted: raw.granted };
    }
    case 'board_surface': {
      if (typeof raw.open !== 'boolean') {
        return null;
      }
      return { type: 'board_surface', seq, open: raw.open };
    }
    case 'board_pointer': {
      if (!Array.isArray(raw.pointers)) {
        return null;
      }
      const pointers: BoardPointerEntry[] = [];
      for (const entry of raw.pointers) {
        if (!isRecord(entry)) {
          return null;
        }
        const entryIdentity = required(entry.identity);
        const x = num(entry.x);
        const y = num(entry.y);
        if (entryIdentity === null || x === null || y === null) {
          return null;
        }
        pointers.push({ identity: entryIdentity, x, y });
      }
      return { type: 'board_pointer', seq, pointers };
    }
    case 'lobby_wait': {
      const room = required(raw.room);
      return room === null ? null : { type: 'lobby_wait', seq, room };
    }
    case 'lobby_waiting': {
      const item = parseLobbyItem(raw);
      return item === null ? null : { type: 'lobby_waiting', seq, item };
    }
    case 'lobby_pending': {
      if (!Array.isArray(raw.items)) {
        return null;
      }
      const items: LobbyPendingItem[] = [];
      for (const entry of raw.items) {
        const item = parseLobbyItem(entry);
        if (item === null) {
          return null;
        }
        items.push(item);
      }
      return { type: 'lobby_pending', seq, items };
    }
    case 'lobby_denied':
      return { type: 'lobby_denied', seq };
    case 'lobby_admitted': {
      // The deferred media credentials (CR-15): token, url and expiry are required; the turn
      // section parses leniently like everywhere else.
      const token = required(raw.token);
      const url = required(raw.url);
      const expiresAt = required(raw.expiresAt);
      if (token === null || url === null || expiresAt === null) {
        return null;
      }
      return {
        type: 'lobby_admitted',
        seq,
        credentials: {
          token,
          url,
          expiresAt,
          turn: parseTurnCredentials(raw.turn),
        },
      };
    }
    case 'lobby_resolved': {
      const identity = required(raw.identity);
      if (identity === null || typeof raw.admitted !== 'boolean') {
        return null;
      }
      return { type: 'lobby_resolved', seq, identity, admitted: raw.admitted };
    }
    default:
      return null;
  }
}

/**
 * Parses a WebSocket text message: JSON first, then the frame shape. Malformed JSON parses to
 * null (the server answers repeated violations itself with 1003).
 */
export function parseFrameText(text: string): ServerFrame | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    return null;
  }
  return parseServerFrame(parsed);
}
